import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Key, Wifi, Calendar, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";
import Header from "@/components/Header";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GuestPortal = () => {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const [portalData, setPortalData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPortalData();
  }, [bookingId]);

  const fetchPortalData = async () => {
    try {
      const response = await axios.get(`${API}/guest-portal/${bookingId}`);
      setPortalData(response.data);
    } catch (error) {
      console.error("Error fetching portal data:", error);
      setError(error.response?.data?.detail || "Access denied");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg text-slate-600">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="pt-32 pb-20">
          <div className="max-w-2xl mx-auto px-4 text-center">
            <h1 className="text-3xl font-heading font-bold text-primary mb-4">
              Access Denied
            </h1>
            <p className="text-lg text-slate-600 mb-8">{error}</p>
            <Button onClick={() => navigate('/')}>Return to Home</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <div className="pt-32 pb-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8"
        >
          <h1 className="text-4xl font-heading font-bold text-primary mb-4" data-testid="guest-portal-title">
            Welcome, {portalData.guest_name}!
          </h1>
          <p className="text-lg text-slate-600 mb-12">
            Here's everything you need for your stay
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
            {/* Door Code */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-secondary rounded-lg p-6 border-2 border-primary/20"
              data-testid="door-code-card"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center">
                  <Key className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-primary">Door Code</h3>
              </div>
              <div className="bg-white rounded p-4 text-center">
                <p className="text-4xl font-bold text-primary font-mono tracking-wider">
                  {portalData.door_code}
                </p>
              </div>
              <p className="text-sm text-slate-600 mt-3">
                Use this code to access the property
              </p>
            </motion.div>

            {/* WiFi Password */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-secondary rounded-lg p-6 border-2 border-primary/20"
              data-testid="wifi-card"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center">
                  <Wifi className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-primary">WiFi</h3>
              </div>
              <div className="bg-white rounded p-4">
                <p className="text-sm text-slate-600 mb-1">Network: KSpace_Guest</p>
                <p className="text-lg font-semibold text-primary font-mono">
                  {portalData.wifi_password}
                </p>
              </div>
            </motion.div>
          </div>

          {/* Stay Details */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-lg border border-border p-6 shadow-sm"
          >
            <h3 className="text-2xl font-heading font-semibold text-primary mb-6">
              Your Stay Details
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex items-start gap-3">
                <Calendar className="w-5 h-5 text-accent mt-1" />
                <div>
                  <p className="text-sm text-slate-600">Check-in</p>
                  <p className="text-lg font-medium text-primary">{portalData.check_in}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Calendar className="w-5 h-5 text-accent mt-1" />
                <div>
                  <p className="text-sm text-slate-600">Check-out</p>
                  <p className="text-lg font-medium text-primary">{portalData.check_out}</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Contact Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="mt-8 bg-accent/10 border border-accent/20 rounded-lg p-6 text-center"
          >
            <h3 className="text-lg font-semibold text-primary mb-2">
              Need Help?
            </h3>
            <p className="text-slate-600 mb-4">
              We're here 24/7 to assist you during your stay
            </p>
            <a 
              href="mailto:info@kspaceproperties.com"
              className="text-accent font-medium hover:underline"
            >
              Contact Us: info@kspaceproperties.com
            </a>
          </motion.div>

          {/* Leave Review Button (shows after checkout) */}
          {new Date(portalData.check_out) < new Date() && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="mt-6"
            >
              <Button
                className="w-full bg-primary hover:bg-primary/90 text-white py-6"
                onClick={() => navigate(`/review/${bookingId}`)}
              >
                <Star className="w-5 h-5 mr-2" />
                Leave a Review
              </Button>
            </motion.div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default GuestPortal;